/**
 * Warteschlange für `ctx.ui.confirm` (Contract: `Promise<boolean>`).
 *
 * Die Shell zeigt Rückfragen als eigenen Dialog statt über `window.confirm`: Eingebettete
 * Browser und Browser mit „weitere Dialoge verhindern“ unterdrücken `window.confirm` still,
 * der Klick wirkte dann wie „Abbrechen“. Fragen mehrere Module (oder ein Modul mehrfach)
 * gleichzeitig, steht immer nur die älteste offene Rückfrage im Dialog; die übrigen warten.
 *
 * Bewusst ohne React, damit die Logik unter `node --test` prüfbar ist.
 */
import type { ConfirmOptions } from '@one/module-sdk';

export interface Rueckfrage {
  /** Fortlaufend; der Dialog antwortet mit dieser Nummer (doppelte Antworten verpuffen). */
  id: number;
  optionen: ConfirmOptions;
}

export interface Rueckfragen {
  /** Stellt eine Rückfrage an; das Promise löst mit `true` (bestätigt) oder `false` auf. */
  fragen(optionen: ConfirmOptions): Promise<boolean>;
  /** Die gerade angezeigte Rückfrage oder `null`. Gleiche Referenz, solange sie offen ist. */
  aktuell(): Rueckfrage | null;
  /** Beantwortet die angezeigte Rückfrage; eine veraltete Nummer wird ignoriert. */
  antworten(id: number, bestaetigt: boolean): void;
  /** Benachrichtigt bei jeder Änderung der Warteschlange. */
  abonnieren(cb: () => void): () => void;
  /** Beantwortet alle offenen Rückfragen mit `false` (Abbau der Shell, Abmeldung). */
  alleAbbrechen(): void;
}

export function erzeugeRueckfragen(): Rueckfragen {
  const schlange: { frage: Rueckfrage; erledigen: (ok: boolean) => void }[] = [];
  const hoerer = new Set<() => void>();
  let nr = 0;
  const melden = () => hoerer.forEach((cb) => cb());

  return {
    fragen(optionen) {
      return new Promise<boolean>((erledigen) => {
        schlange.push({ frage: { id: ++nr, optionen }, erledigen });
        melden();
      });
    },
    aktuell: () => schlange[0]?.frage ?? null,
    antworten(id, bestaetigt) {
      const erste = schlange[0];
      if (!erste || erste.frage.id !== id) return;
      schlange.shift();
      erste.erledigen(bestaetigt);
      melden();
    },
    abonnieren(cb) {
      hoerer.add(cb);
      return () => hoerer.delete(cb);
    },
    alleAbbrechen() {
      // Sonst bliebe das Promise eines Moduls für immer offen, wenn die Shell abgebaut wird.
      for (const offen of schlange.splice(0)) offen.erledigen(false);
      melden();
    },
  };
}

/** Beschriftung der beiden Knöpfe; leere Angaben fallen auf die Standardtexte zurück. */
export function knopfTexte(optionen: ConfirmOptions): { bestaetigen: string; abbrechen: string } {
  return {
    bestaetigen: optionen.confirmLabel?.trim() || 'Bestätigen',
    abbrechen: optionen.cancelLabel?.trim() || 'Abbrechen',
  };
}

/**
 * Tastatur im Dialog: Esc bricht ab, Enter bestätigt. Steht der Fokus auf einem Knopf oder
 * Link, löst Enter dort den Knopf selbst aus (z. B. „Abbrechen“) — dann nichts zusätzlich tun.
 * Während einer IME-Eingabe (`isComposing`) gehört Enter der Eingabe.
 */
export function tastenAktion(
  taste: string,
  zielIstBedienelement: boolean,
  isComposing = false,
): 'bestaetigen' | 'abbrechen' | null {
  if (taste === 'Escape') return 'abbrechen';
  if (taste === 'Enter' && !zielIstBedienelement && !isComposing) return 'bestaetigen';
  return null;
}
